import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Snackbar
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PricingRule, TimeBlockPrice } from '../../models/types';
import {
  getPricingRules,
  addPricingRule,
  updatePricingRule,
  deletePricingRule,
  getPricingRulesByVesselId,
  addPricingRule as addVesselPricingRule,
  updatePricingRule as updateVesselPricingRule,
  deletePricingRule as deleteVesselPricingRule
} from '../../firebase/services/pricingService';
import { getVesselsByType, getActiveVessels } from '../../firebase/services/vesselService';
import { Vessel } from '../../models/types';
import MarinaAdminLayout from '../../layouts/MarinaAdminLayout';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pricing-tabpanel-${index}`}
      aria-labelledby={`pricing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function PricingManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);
  const [vesselPricingRules, setVesselPricingRules] = useState<PricingRule[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRule, setSelectedRule] = useState<PricingRule | null>(null);
  const [formData, setFormData] = useState<Partial<PricingRule>>({
    vesselType: '',
    vesselId: '',
    basePrice: 0,
    timeBlockPrices: [],
    daysOfWeek: []
  });
  const [newTimeBlock, setNewTimeBlock] = useState<TimeBlockPrice>({ hours: 1, price: 0 });
  const [vesselTypes, setVesselTypes] = useState<string[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [vesselsByType, setVesselsByType] = useState<{ [key: string]: Vessel[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);

  useEffect(() => {
    loadPricingData();
  }, []);

  const loadPricingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all active vessels and their types
      const vesselsByTypeData = await getVesselsByType();
      const types = Object.keys(vesselsByTypeData).filter(type => vesselsByTypeData[type].length > 0);
      setVesselTypes(types);
      setVesselsByType(vesselsByTypeData);
      
      // Get all active vessels for the dropdown
      const allVessels = await getActiveVessels();
      setVessels(allVessels);

      // Get pricing rules for vessel types
      const rules = await getPricingRules();
      setPricingRules(rules);
      
      // Get pricing rules for specific vessels
      const vesselRulesPromises = allVessels.map(vessel => getPricingRulesByVesselId(vessel.id));
      const vesselRulesResults = await Promise.all(vesselRulesPromises);
      const allVesselRules = vesselRulesResults.flat();
      setVesselPricingRules(allVesselRules);
    } catch (error) {
      console.error('Error loading pricing data:', error);
      setError('Failed to load pricing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (rule?: PricingRule) => {
    if (rule) {
      setSelectedRule(rule);
      setFormData(rule);
    } else {
      setSelectedRule(null);
      setFormData({
        vesselType: '',
        vesselId: '',
        basePrice: 0,
        timeBlockPrices: [],
        daysOfWeek: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRule(null);
    setFormData({
      vesselType: '',
      vesselId: '',
      basePrice: 0,
      timeBlockPrices: [],
      daysOfWeek: []
    });
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<string>) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.checked
    });
  };

  const handleDateChange = (field: string) => (date: Date | null) => {
    setFormData({
      ...formData,
      [field]: date ? date.toISOString() : null
    });
  };

  const handleAddTimeBlock = () => {
    if (editingBlockIndex !== null) {
      const updatedBlocks = [...(formData.timeBlockPrices || [])];
      updatedBlocks[editingBlockIndex] = {
        ...updatedBlocks[editingBlockIndex],
        price: newTimeBlock.price
      };
      setFormData({ ...formData, timeBlockPrices: updatedBlocks });
      setEditingBlockIndex(null);
    } else {
      setFormData({
        ...formData,
        timeBlockPrices: [...(formData.timeBlockPrices || []), { ...newTimeBlock }],
      });
    }
    setNewTimeBlock({ hours: 1, price: 0 });
  };

  const handleRemoveTimeBlock = (index: number) => {
    setFormData(prev => ({
      ...prev,
      timeBlockPrices: (prev.timeBlockPrices || []).filter((_, i) => i !== index)
    }));
  };

  const handleTimeBlockChange = (field: keyof TimeBlockPrice) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewTimeBlock(prev => ({
      ...prev,
      [field]: Number(event.target.value)
    }));
  };

  const handleEditTimeBlock = (index: number) => {
    setEditingBlockIndex(index);
    setNewTimeBlock({ ...formData.timeBlockPrices[index] });
  };

  const handleSubmit = async () => {
    try {
      if (selectedRule) {
        if (selectedRule.vesselId) {
          await updateVesselPricingRule(selectedRule.id, formData as PricingRule);
        } else {
          await updatePricingRule(selectedRule.id, formData as PricingRule);
        }
      } else {
        if (formData.vesselId) {
          await addVesselPricingRule(formData as PricingRule);
        } else {
          await addPricingRule(formData as PricingRule);
        }
      }
      handleCloseDialog();
      loadPricingData();
    } catch (error) {
      console.error('Error saving pricing rule:', error);
      setError('Failed to save pricing rule. Please try again.');
    }
  };

  const handleDelete = async (id: string, isVesselSpecific: boolean) => {
    try {
      if (isVesselSpecific) {
        await deleteVesselPricingRule(id);
      } else {
        await deletePricingRule(id);
      }
      loadPricingData();
    } catch (error) {
      console.error('Error deleting pricing rule:', error);
      setError('Failed to delete pricing rule. Please try again.');
    }
  };

  const renderPricingCard = (rule: PricingRule, isVesselSpecific: boolean) => {
    const vessel = rule.vesselId ? vessels.find(v => v.id === rule.vesselId) : null;
    
    return (
      <Grid
        key={rule.id}
        item xs={12} sm={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
                {isVesselSpecific 
                  ? `${vessel?.name || 'Unknown Vessel'} (${vessel?.type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') || 'Unknown Type'})`
                  : rule.vesselType.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
              </Typography>
              <Box>
                <IconButton size="small" onClick={() => handleOpenDialog(rule)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(rule.id, isVesselSpecific)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1">Base Price: ${rule.basePrice}</Typography>
              <Typography variant="body2" color="text.secondary">
                {rule.startDate && (
                  `${new Date(rule.startDate).toLocaleDateString()} - 
                  ${rule.endDate ? new Date(rule.endDate).toLocaleDateString() : 'Ongoing'}`
                )}
              </Typography>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {rule.timeBlockPrices.map((block, index) => (
                <Typography key={index} variant="body2" sx={{ 
                  bgcolor: 'action.hover', 
                  px: 1, 
                  py: 0.5, 
                  borderRadius: 1 
                }}>
                  {block.hours}h: ${block.price}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </MarinaAdminLayout>
    );
  }

  return (
    <MarinaAdminLayout>
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}
        
        <Typography variant="h4" gutterBottom>
          Pricing Management
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Vessel Type Pricing" />
            <Tab label="Vessel-Specific Pricing" />
          </Tabs>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ mr: 2 }}
          >
            Add {tabValue === 0 ? 'Vessel Type Pricing' : 'Vessel-Specific Pricing'}
          </Button>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
            {pricingRules.map(rule => renderPricingCard(rule, false))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
            {vesselPricingRules.map(rule => renderPricingCard(rule, true))}
          </Grid>
        </TabPanel>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedRule ? 'Edit Pricing Rule' : 'New Pricing Rule'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {tabValue === 0 ? (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Vessel Type</InputLabel>
                      <Select
                        value={formData.vesselType || ''}
                        label="Vessel Type"
                        onChange={handleSelectChange('vesselType')}
                      >
                        {vesselTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Vessel</InputLabel>
                      <Select
                        value={formData.vesselId || ''}
                        label="Vessel"
                        onChange={handleSelectChange('vesselId')}
                      >
                        {vessels.map((vessel) => (
                          <MenuItem key={vessel.id} value={vessel.id}>
                            {vessel.name} ({vessel.type})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Base Price"
                    value={formData.basePrice}
                    onChange={handleInputChange('basePrice')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Time Block Pricing</Typography>
                  <List>
                    {formData.timeBlockPrices?.map((block, index) => (
                      <ListItem key={index}>
                        <ListItemText 
                          primary={`${block.hours} ${block.hours === 1 ? 'hour' : 'hours'}: $${block.price}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleEditTimeBlock(index)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton edge="end" onClick={() => handleRemoveTimeBlock(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField
                      type="number"
                      label="Hours"
                      value={newTimeBlock.hours}
                      onChange={handleTimeBlockChange('hours')}
                      sx={{ width: '30%' }}
                      disabled={editingBlockIndex !== null}
                    />
                    <TextField
                      type="number"
                      label="Price"
                      value={newTimeBlock.price}
                      onChange={handleTimeBlockChange('price')}
                      sx={{ width: '40%' }}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleAddTimeBlock}
                      sx={{ width: '30%' }}
                    >
                      {editingBlockIndex !== null ? 'Save' : 'Add Block'}
                    </Button>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}>
                  <DatePicker
                    label="Start Date"
                    value={formData.startDate ? new Date(formData.startDate) : null}
                    onChange={handleDateChange('startDate')}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}>
                  <DatePicker
                    label="End Date"
                    value={formData.endDate ? new Date(formData.endDate) : null}
                    onChange={handleDateChange('endDate')}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.id}
                        onChange={handleSwitchChange('id')}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {selectedRule ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
        
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </MarinaAdminLayout>
  );
} 